import * as React from 'react';
import { TextField, InputAdornment, ThemeProvider, createTheme, Popper, Checkbox, Box, IconButton, InputLabel, Chip, Paper, Button, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import { colors } from '../../common/pallette/colors';
import styled from 'styled-components';
import { genUID } from '../../common/StaticExports';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

// Base theme used by ALL autocomplete instances
const baseThemeConfig: any = {
  MuiOutlinedInput: {
    styleOverrides: {
      root: { padding: '1px 2px !important' },
      notchedOutline: { borderColor: colors.black.dark },
    },
  },
  MuiStack: {
    styleOverrides: { root: { width: '100% !important' } },
  },
  MuiInputBase: {
    styleOverrides: {
      input: { fontSize: '12px' },
      root: { '& .Mui-disabled': { cursor: 'not-allowed' } },
    },
  },
  MuiAutocomplete: {
    styleOverrides: { inputRoot: { paddingRight: '0px !important' } },
  },
};

// Default theme hides chips (legacy text-display mode)
const defaultTheme = createTheme({
  components: {
    ...baseThemeConfig,
    MuiChip: { styleOverrides: { root: { display: 'none' } } },
  },
});

// Chip-mode theme — shows inline chips for multi-select (single row, limited tags)
const chipTheme = createTheme({
  components: {
    ...baseThemeConfig,
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '2px 4px !important',
          flexWrap: 'nowrap',
          gap: '3px',
          alignItems: 'center',
          height: '34px',
          overflow: 'hidden',
        },
        notchedOutline: { borderColor: colors.black.dark },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          display: 'inline-flex',
          height: '20px',
          fontSize: '11px',
          maxWidth: '120px',
          flexShrink: 0,
          backgroundColor: `${colors.blue.navy}12`,
          color: colors.blue.navy,
          border: `1px solid ${colors.blue.navy}30`,
          '& .MuiChip-label': { padding: '0 6px' },
          '& .MuiChip-deleteIcon': {
            fontSize: '13px',
            color: `${colors.blue.navy}60`,
            margin: '0 4px 0 -2px',
            '&:hover': { color: colors.blue.navy },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: { paddingRight: '0px !important' },
        input: { minWidth: '50px !important', width: '50px !important', fontSize: '12px' },
      },
    },
  },
});

const CustomPopper = styled(Popper)({
  '& .MuiAutocomplete-listbox': { fontSize: '14px' },
});

export default function AutoComplete(props: any) {
  const {
    placeholder,
    selectedValues,
    options,
    variant,
    key,
    handleMenuClick,
    label,
    clickReset,
    isMultiple,
    addnone,
    isReadOnly,
    isError,
    errorMessage,
    showLabelBadge,
    // showChips: render selected items as inline chips instead of concatenated text
    showChips,
    // onChipRemove: called with the item when a chip's × is clicked
    onChipRemove,
  } = props;

  const [optionList, setOptionList] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const keyLabel = key ? key : 'name';
  const [t] = useTranslation();

  React.useEffect(() => {
    if (options !== undefined) setOptionList(options);
  }, [options]);

  const handleInputChange = (_event: any, newInputValue: any) => {
    setInputValue(newInputValue);
  };

  const isOptionSelected = (option: any) => {
    if (isMultiple) {
      return (
        Array.isArray(selectedValues) &&
        selectedValues?.some(
          (obj: any) =>
            obj === option?.value ||
            obj === option?.id ||
            obj === option ||
            (typeof obj === 'object' &&
              obj?.id === option?.id &&
              obj?.id != undefined &&
              option?.id != null)
        )
      );
    } else {
      if (typeof selectedValues === 'object' && selectedValues !== null) {
        return selectedValues?.id === option?.id;
      }
      return (
        selectedValues === option?.value ||
        selectedValues === option?.id ||
        selectedValues === option
      );
    }
  };

  // Builds comma-joined display text from selected values + option list
  const getSelectedText = () => {
    if (isMultiple) {
      const filteredList = optionList?.filter(
        (opt: any) =>
          (Array.isArray(selectedValues) &&
            selectedValues.some(
              (sv: any) =>
                sv?.id !== undefined && opt?.id !== undefined && sv.id === opt.id
            )) ||
          selectedValues?.includes(opt?.value) ||
          selectedValues?.includes(String(opt?.id)) ||
          selectedValues?.includes(opt?.id) ||
          selectedValues?.includes(opt)
      );
      return filteredList
        ?.map((option: any) => option?.displayName || option?.name || option)
        .join(', ');
    } else {
      if (typeof selectedValues === 'object' && selectedValues !== null) {
        return selectedValues?.displayName || selectedValues?.name || '';
      }
      const selectedOption: any = optionList?.find(
        (opt: any) =>
          opt?.id === selectedValues ||
          opt?.value === selectedValues ||
          opt === selectedValues
      );
      return selectedOption?.displayName || selectedOption?.name || selectedOption || '';
    }
  };

  const activeTheme = showChips && isMultiple ? chipTheme : defaultTheme;

  return (
    <ThemeProvider theme={activeTheme}>
      <Stack spacing={0} sx={{ gap: '4px' }}>
        {label && label !== '' && (
          <InputLabel
            className="fs12"
            sx={{
              textAlign: 'left',
              color: colors.black.dark,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {label}
            {showLabelBadge &&
              isMultiple &&
              Array.isArray(selectedValues) &&
              selectedValues.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: colors.blue.navy,
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 600,
                    lineHeight: 1,
                    padding: '0 3px',
                    flexShrink: 0,
                  }}
                >
                  {selectedValues.length}
                </Box>
              )}
          </InputLabel>
        )}
        <Autocomplete
          multiple={isMultiple}
          disableCloseOnSelect={isMultiple}
          limitTags={showChips ? 2 : undefined}
          getLimitTagsText={(more) => (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '20px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: colors.blue.navy,
                color: '#fff',
                borderRadius: '16px',
                padding: '0 7px',
                flexShrink: 0,
                lineHeight: 1,
                cursor: 'default',
              }}
            >
              +{more}
            </Box>
          )}
          className="fs12"
          value={isMultiple ? selectedValues || [] : selectedValues || null}
          inputValue={inputValue}
          open={open}
          disabled={isReadOnly}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onInputChange={handleInputChange}
          options={optionList || []}
          getOptionLabel={(option: any) =>
            option?.displayName || option?.name || option || ''
          }
          fullWidth
          sx={{ border: 0 }}
          PopperComponent={CustomPopper}
          PaperComponent={
            showChips && isMultiple
              ? (paperProps: any) => {
                  const count = Array.isArray(selectedValues) ? selectedValues.length : 0;
                  const total = optionList?.length ?? 0;
                  return (
                    <Paper {...paperProps}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid rgba(0,0,0,0.08)',
                          backgroundColor: '#f8f9fb',
                          minHeight: '32px',
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: count > 0 ? colors.blue.navy : 'text.secondary',
                            fontWeight: count > 0 ? 600 : 400,
                            fontSize: '11px',
                          }}
                        >
                          {count > 0 ? `${count} of ${total} selected` : `${total} available`}
                        </Typography>
                        {count > 0 && (
                          <Button
                            size="small"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              clickReset && clickReset();
                            }}
                            sx={{
                              fontSize: '11px',
                              color: colors.blue.navy,
                              textTransform: 'none',
                              padding: '0 6px',
                              minWidth: 0,
                              fontWeight: 500,
                              '&:hover': { backgroundColor: `${colors.blue.navy}10` },
                            }}
                          >
                            Clear
                          </Button>
                        )}
                      </Box>
                      {paperProps.children}
                    </Paper>
                  );
                }
              : undefined
          }
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof value === 'object' && value !== null) {
              return option?.id === value?.id;
            }
            return (
              option?.id === value || option?.value === value || option === value
            );
          }}
          filterOptions={(options, params) => {
            const filtered = options?.filter((option: any) =>
              option[keyLabel].toLowerCase().includes(params.inputValue.toLowerCase())
            );
            return filtered;
          }}
          // Chip-mode: render each selected item as a dismissible chip
          renderTags={
            showChips && isMultiple
              ? (tagValue, _getTagProps) =>
                  tagValue.map((option: any, index: number) => (
                    <Chip
                      key={option?.id ?? index}
                      label={option?.displayName || option?.name || String(option)}
                      size="small"
                      onDelete={() => onChipRemove && onChipRemove(option)}
                    />
                  ))
              : undefined
          }
          renderOption={(props: any, option, { selected }) => {
            const isFirstOption = props?.['data-option-index'] === 0;
            return (
              <>
                {addnone && isFirstOption && (
                  <li {...props} onClick={() => handleMenuClick(undefined)}>
                    {t('NONE')}
                  </li>
                )}
                <li {...props} onClick={() => handleMenuClick(option)}>
                  {isMultiple && (
                    <Checkbox
                      size="small"
                      style={{ marginRight: 8 }}
                      checked={isOptionSelected(option)}
                    />
                  )}
                  {Object.keys(option).includes('uid')
                    ? option[keyLabel] + ' (' + genUID(option['uid']) + ')'
                    : option[keyLabel]}
                </li>
              </>
            );
          }}
          renderInput={(params) => {
            const selectedText = getSelectedText();

            // Chip mode: MUI handles the display; just pass the live search text
            // Text mode: show typed input while searching, selected text when idle
            const displayValue = showChips
              ? inputValue
              : open && inputValue
              ? inputValue
              : selectedText;

            return (
              <TextField
                {...params}
                sx={{ border: 0 }}
                fullWidth
                error={isError}
                helperText={isError ? errorMessage : ''}
                placeholder={
                  showChips
                    ? 'Search...'
                    : selectedText
                    ? ''
                    : placeholder || ''
                }
                className="fs12"
                variant={variant || 'outlined'}
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        className="cursorPointer"
                        onClick={() => setOpen(!open)}
                        sx={{ display: 'flex', flexDirection: 'column' }}
                      >
                        <FontAwesomeIcon
                          className="icon"
                          fontSize="12px"
                          icon={faBars}
                        />
                        <FontAwesomeIcon
                          className="icon"
                          fontSize="12px"
                          icon={faCaretDown}
                        />
                      </Box>
                      {!isReadOnly && !showChips && (
                        <IconButton onClick={() => clickReset()}>
                          <ClearIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                  inputProps: {
                    ...params.inputProps,
                    // In chip mode MUI manages the input value; don't override
                    ...(!showChips && { value: displayValue }),
                    style: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  },
                }}
              />
            );
          }}
        />
      </Stack>
    </ThemeProvider>
  );
}
